import type { StructuredQuery } from "@shared/labs/sourcing";
import { ArrowRight, Languages, Sparkles } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { type LaScopeSelection, SourcingModelPicker } from "./model-picker";

const EXAMPLES = [
  {
    flag: "🇧🇷",
    text: "Analista de folha de pagamento em São Paulo, eSocial e Protheus, 3+ anos",
  },
  {
    flag: "🇲🇽",
    text: "Ingeniero de datos senior en CDMX, Spark y dbt, experiencia en fintech",
  },
  {
    flag: "🇺🇸",
    text: "Staff backend engineer, Go + Kubernetes, US-remote, payments background",
  },
  {
    flag: "👥",
    text: "Perfis parecidos com linkedin.com/in/algum-engenheiro",
  },
];

/**
 * Step 1 — the brief. The recruiter describes the role in plain language; the
 * "understand" usecase maps it to a structured, editable query. Cmd/Ctrl+Enter
 * submits.
 */
export function BriefStep({
  brief,
  setBrief,
  onParsed,
  laScope,
  setLaScope,
  enableLevel = false,
}: {
  brief: string;
  setBrief: (v: string) => void;
  onParsed: (q: StructuredQuery) => void;
  /** LA-model scope picker. When provided, the in-scope competencies (and, in the
   *  explore flow, the target level) enrich the understand pass. */
  laScope?: LaScopeSelection;
  setLaScope?: (s: LaScopeSelection) => void;
  /** Show the target-level selector in the picker (Explore flow). */
  enableLevel?: boolean;
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const empty = brief.trim().length === 0;

  async function parse() {
    if (empty || loading) return;
    setLoading(true);
    try {
      const query = await rpcClient.labs.sourcingUnderstand({
        brief,
        laModelId: laScope?.modelId ?? undefined,
        laDisciplineId: laScope?.disciplineId ?? undefined,
        laLevel: laScope?.level ?? undefined,
      });
      onParsed(query);
    } catch (err) {
      toast({
        title: "Falha ao ler o brief",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="space-y-6">
      <div>
        <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          Etapa 01 — o brief
        </span>
        <h2 className="mt-2 text-xl font-semibold tracking-tight">Como é o candidato ideal?</h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
          Descreva a vaga como descreveria para um colega,{" "}
          <strong>cole uma descrição de vaga (JD) completa</strong>, ou peça{" "}
          <strong>perfis parecidos com uma pessoa</strong> (cole o link do LinkedIn). O modelo
          identifica o tipo, lê a intenção e monta filtros editáveis na próxima etapa.
        </p>
      </div>

      {laScope && setLaScope && (
        <SourcingModelPicker value={laScope} onChange={setLaScope} enableLevel={enableLevel} />
      )}

      <Card>
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            <Languages size={13} /> pt-BR · español · english
          </span>
          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
            {brief.length} caracteres
          </span>
        </div>
        <CardContent className="p-0">
          <Textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") parse();
            }}
            rows={6}
            placeholder="Ex.: Analista de folha de pagamento em São Paulo, eSocial e Protheus, 3+ anos…"
            className="resize-y rounded-none border-0 px-4 py-4 text-base leading-relaxed focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </CardContent>
      </Card>

      <div className="space-y-2.5">
        <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          Experimente um destes
        </span>
        <div className="flex flex-col gap-2">
          {EXAMPLES.map((ex) => (
            <button
              key={ex.text}
              type="button"
              onClick={() => setBrief(ex.text)}
              className="group flex items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-2.5 text-left transition-colors hover:border-ring"
            >
              <span className="text-base leading-none">{ex.flag}</span>
              <span className="flex-1 text-sm text-muted-foreground group-hover:text-foreground">
                {ex.text}
              </span>
              <ArrowRight
                size={15}
                className="shrink-0 text-muted-foreground opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100"
              />
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border pt-5">
        <span className="hidden font-mono text-[11px] text-muted-foreground sm:inline">
          ⌘ / Ctrl + Enter para ler
        </span>
        <Button onClick={parse} disabled={loading || empty}>
          {loading ? (
            <>
              <Sparkles className="animate-pulse" />
              Lendo o brief…
            </>
          ) : (
            <>
              <Sparkles />
              Entenda o brief
              <ArrowRight />
            </>
          )}
        </Button>
      </div>
    </section>
  );
}
